import { getApiBaseUrl } from "./baseUrl";

const API_URL = getApiBaseUrl();

/**
 * Secure document download helper.
 *
 * The document password is NEVER placed in a URL query string (it would leak
 * into browser history, proxy logs and server logs). Instead:
 *   1. POST /api/uploads/files/:id/access  { password }  -> short-lived token
 *   2. GET  /api/uploads/files/download/:id?accessToken=<token>
 *
 * For non-classified documents step 1 answers { required: false } and the
 * download proceeds directly.
 */
export async function fetchFileBlob({ fileId, password = "", token }) {
  if (!fileId) throw new Error("File ID is missing.");

  let accessToken = null;
  const accessRes = await fetch(`${API_URL}/api/uploads/files/${fileId}/access`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ password }),
  });

  if (accessRes.ok) {
    const data = await accessRes.json().catch(() => ({}));
    if (data?.required && data?.accessToken) accessToken = data.accessToken;
  } else if (accessRes.status === 403) {
    // Wrong password or not authorized — surface a clear error.
    let msg = "Invalid password";
    try {
      const err = await accessRes.json();
      msg = err.message || msg;
    } catch { }
    throw new Error(msg);
  } else if (accessRes.status !== 404) {
    // 404 just means the access endpoint isn't reachable (older backend);
    // fall through and try the plain download below.
    throw new Error(`Access check failed (${accessRes.status})`);
  }

  const url = new URL(`${API_URL}/api/uploads/files/download/${fileId}`);
  if (accessToken) url.searchParams.set("accessToken", accessToken);

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    let msg = `Failed to fetch file (${res.status})`;
    try {
      const err = await res.json();
      msg = err.message || msg;
    } catch { }
    throw new Error(msg);
  }
  return res.blob();
}
